import { message } from 'antd';
import jwtDecode from 'jwt-decode';
import _ from 'lodash';
import moment from 'moment';
import { history } from 'umi';

import { bindServices } from '@/services/api';

const api = bindServices('external');
import { formFieldBehaviorsMapping } from '@/utils/core/business-logic';
import { isJsonLike, parseJson, translate } from '@/utils/infrastructure';
import { getToken } from '@/utils/infrastructure/communication';
import { CoreEnums } from '@/utils/domain/constants';
import { authProcess, externalHandle, getProcessConfig, sendProcessForm } from './service';
import PropTypes from 'prop-types';

const initialState = {
  done: false,
  config: null,
  processId: null,
  validationErrors: [],
  telegramAuthorize: null,
};

const Model = {
  namespace: 'external',
  state: initialState,
  effects: {
    *authProcess({ payload }, { call, put, select }) {
      const token = getToken();

      const publicCode = _.get(history.location, 'query.publicCode');
      const keyId = payload.code ?? publicCode;
      const pKeyId = `x-${payload.type}-processId"${keyId}"`;
      let pId = localStorage.getItem(pKeyId);

      const { newToken, process, processInstance } = yield select((state) => state.external);

      try {
        if (!token) {
          const auth = newToken
            ? { token: newToken, process, processInstance }
            : yield call(authProcess, payload);

          localStorage.setItem(`x-auth-${payload.type}Token"${keyId}"`, JSON.stringify(auth.token));
          localStorage.setItem(`x-${payload.type}-processId"${keyId}"`, _.get(auth.process, 'id'));

          if (auth.processInstance) {
            localStorage.setItem(
              `x-${payload.type}-processInstance"${keyId}"`,
              JSON.stringify(auth.processInstance),
            );
          }

          pId = _.get(auth.process, 'id');
        }

        const res = payload.shouldInit ? yield call(getProcessConfig, pId) : null;

        yield put({
          type: 'queryProcessConfig',
          payload: {
            ...(res && { res }),
            code: keyId,
            pId,
            type: payload.type,
          },
        });
      } catch (e) {}
    },

    *sendProcessForm({ payload }, { call, put }) {
      try {
        const code = _.get(history.location, 'query.publicCode');
        const params = {
          ...payload,
          processId: localStorage.getItem(`x-public-processId"${code}"`),
        };
        const res = yield call(sendProcessForm, params);
        yield put({ type: 'queryProcessFormSent', payload: res });
      } catch (e) {
        if (e?.validationErrors) {
          yield put({
            type: 'queryFormSentError',
            payload: e.validationErrors,
          });
        }
      }
    },

    *handle({ payload }, { call, put }) {
      try {
        const token = getToken({ global: true }) || {};
        const res = yield call(externalHandle, { ...payload, token: token?.accessToken });
        yield put({ type: 'queryExternalHandle', payload: res });
      } catch (err) {
        if (err && err.code !== '301') {
          let message = isJsonLike(err.message) ? JSON.parse(err.message)?.Message : err.message;
          if (!message && err?.clone) {
            try {
              const e = yield err.clone().json();
              const bm = parseJson(e.message);
              message = bm?.Message ?? e.message ?? e.Message;
            } catch {}
          }
          yield put({ type: 'global/withError', payload: message });
          history.replace(`/external/400`);
        }
      }
    },

    *checkTelegramAuthorize({ payload }, { call, put }) {
      try {
        yield call(api.getById, { route: 'providers/telegram/authorize', ...payload });
        yield put({ type: 'queryTelegramAuthorize', payload: true });
      } catch (e) {
        if (e?.status === 401) {
          message.error(translate('form.telegram.unauthorized'), 5);
          history.replace('/');
        }
      }
    },

    *telegramAuthorize({ payload }, { call, put }) {
      try {
        yield call(api.create, { route: `providers/telegram/authorize`, data: payload });
        message.success(translate('form.telegram.message'), 5);
        history.replace('/');
      } catch (e) {}
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'reset', payload });
    },
  },
  reducers: {
    queryProcessConfig(state, { payload }) {
      const token = localStorage.getItem(`x-auth-${payload.type}Token"${payload.code}"`);
      const user = jwtDecode(token);

      moment.locale(user.language || 'es-pe');
      moment.tz.setDefault(user.timeZone);

      return {
        ...state,
        config: {
          ...payload.res,
          processId: payload.pId,
          ...(payload.res && {
            behaviorCases: formFieldBehaviorsMapping(payload.res),
          }),
        },
      };
    },

    queryProcessFormSent(state, { payload }) {
      return {
        ...initialState,
        done: payload?.processInstance || {},
      };
    },

    queryFormSentError(state, { payload }) {
      return {
        ...state,
        validationErrors: payload,
      };
    },

    queryExternalHandle(state, { payload }) {
      const { command, redirectRoute, autoFire, ...data } = payload;

      if (autoFire && command.command !== CoreEnums.RequestDetailCommandType.Details) {
        //  set temporally variable to persist the auto command execution
        sessionStorage.setItem('x-autoFireCommand', JSON.stringify(command));
      }

      return {
        ...state,
        ...data,
        redirectTo: redirectRoute,
      };
    },

    queryTelegramAuthorize(state, { payload }) {
      return {
        ...state,
        telegramAuthorize: payload,
      };
    },

    reset() {
      return initialState;
    },
  },
};

export default Model;
